import { useEffect } from "react";
import { ThreePlanogram } from "../store/types";
import { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { Products } from "./Products";
import { Color } from "src/elements";
import { createThreeInstancedBoxes, createThreeMesh } from "../store/utils/three";
import { getBoardDrawing, isCollidingRackBoard, rackNameOf } from "@CommonUtils/pog/racks";
import { getDeadSpans } from "@CommonUtils/pog/shelfMerchandising";
import {
  DEFAULT_DRAWN_DIVIDER_HEIGHT,
  getActiveShelfDividers,
  getDividerDrawSize,
} from "@CommonUtils/pog/shelfDividers";
import {
  DRAWN_PUSHER_PLATE_DEPTH,
  getShelfLoaderTrack,
  getShelfPusherPlates,
  LOADER_TRACK_COLOR,
  LOADER_TRACK_TOP_COLOR,
  PUSHER_PLATE_COLOR,
} from "src/components/Planogram/common/shelfLoaderView";
import { useSplitShift } from "../store/splitShift";
import {
  OVERHANG_COLOR,
  OVERHANG_INVALID_COLOR,
  shelfFrontOverhangMarker,
} from "src/components/Planogram/common/frontOverhangView";

interface Props {
  shelf: PlanogramShelf;
  showDividers?: boolean;
  three: ThreePlanogram;
}

/** A rack's tiers: distinct from a shelf's board, quietly (as on the 2D canvas). */
export const RACK_BOARD_COLOR = Color.gray600;
export const Shelf = ({ shelf, showDividers = true, three }: Props) => {
  const toSceneX = useSplitShift();
  useEffect(() => {
    // Not drawn where a board beside it or a rack over it fills its dead span.
    const drawing = shelf.bay.pallet ? null : getBoardDrawing(shelf);
    const { mesh, cleanUp } = createThreeMesh({
      scene: three.scene,
      width: drawing ? drawing.width : shelf.width,
      height: shelf.thickness,
      depth: shelf.depth,
      x: toSceneX(shelf.getXLeft()) + (drawing ? drawing.x : 0),
      y: shelf.getYBottom(),
      z: shelf.getZBack(),
      color: rackNameOf(shelf.assembly) ? RACK_BOARD_COLOR : Color.forest950,
      renderOrder: 2,
    });

    mesh.userData = {
      type: "shelf",
      shelf,
    };

    return cleanUp;
  }, [shelf, three, toSceneX]);

  useEffect(() => {
    const marker = shelfFrontOverhangMarker(shelf);
    if (!marker) return;
    const { mesh, cleanUp } = createThreeMesh({
      scene: three.scene,
      width: shelf.width,
      height: Math.min(12, shelf.thickness),
      depth: 4,
      x: toSceneX(shelf.getXLeft()),
      y: shelf.getYBottom() + shelf.thickness / 2,
      z: shelf.getZBack() + shelf.depth + 1,
      color: marker.invalid ? OVERHANG_INVALID_COLOR : OVERHANG_COLOR,
      unlit: true,
      renderOrder: 4,
    });
    mesh.userData = { type: "shelf", shelf };
    return cleanUp;
  }, [shelf, three, toSceneX]);

  useEffect(() => {
    // A pallet's lanes share the deck, so a lane's strips are its own.
    const drawing = shelf.bay.pallet ? null : getBoardDrawing(shelf);
    // A colliding rack board is a data error: flagged whole, so it shows.
    const spans = !drawing
      ? getDeadSpans(shelf)
      : isCollidingRackBoard(shelf)
        ? [{ x: drawing.x, width: drawing.width }]
        : drawing.flaggedDeadSpans;
    const cleanUps = spans.map(
      (span) =>
        createThreeMesh({
          scene: three.scene,
          width: span.width,
          height: shelf.thickness + 2,
          depth: shelf.depth + 2,
          x: toSceneX(shelf.getXLeft()) + span.x,
          y: shelf.getYBottom() - 1,
          z: shelf.getZBack() - 1,
          color: Color.alertMediumIcon,
          opacity: 0.7,
          renderOrder: 3,
        }).cleanUp,
    );

    return () => cleanUps.forEach((cleanUp) => cleanUp());
  }, [shelf, three, toSceneX]);

  useEffect(() => {
    const dividers = getActiveShelfDividers(shelf.dividers);
    if (!showDividers || !dividers) return;
    const dividerXs = shelf.items.flatMap((item) => item.getLayout().dividerXs);
    if (dividerXs.length === 0) return;
    const size = getDividerDrawSize(dividers, shelf.depth, DEFAULT_DRAWN_DIVIDER_HEIGHT);
    const y = shelf.getYBottom() + shelf.thickness;
    const z = Math.max(0, shelf.depth - size.depth);

    return createThreeInstancedBoxes({
      scene: three.scene,
      ...size,
      positions: dividerXs.map((x) => ({ x: toSceneX(x), y, z })),
      color: Color.gray400,
      renderOrder: 3,
    }).cleanUp;
  }, [shelf, showDividers, three, toSceneX]);

  // Spring loader as visible hardware: a dark track slab over the whole unusable back band, and a
  // pusher paddle behind each facing column, flat against the back of the stock (which Product
  // front-aligns). Both unlit.
  useEffect(() => {
    const track = getShelfLoaderTrack(shelf);
    if (!track) return;
    const plates = getShelfPusherPlates(shelf);
    const trackCleanUp = createThreeMesh({
      scene: three.scene,
      ...track,
      x: toSceneX(track.x),
      color: LOADER_TRACK_COLOR,
      topColor: LOADER_TRACK_TOP_COLOR,
      renderOrder: 3,
      unlit: true,
    }).cleanUp;
    const cleanUps = plates.map(
      (plate) =>
        createThreeMesh({
          scene: three.scene,
          width: plate.width,
          height: plate.height,
          depth: DRAWN_PUSHER_PLATE_DEPTH,
          x: toSceneX(plate.x),
          y: plate.y,
          z: plate.z,
          color: PUSHER_PLATE_COLOR,
          renderOrder: 3,
          unlit: true,
        }).cleanUp,
    );

    return () => [trackCleanUp, ...cleanUps].forEach((cleanUp) => cleanUp());
  }, [shelf, three, toSceneX]);

  return <Products products={shelf.items} three={three} />;
};
